import { useState } from 'react';
import type { ProductSpec } from '../../productintel/framework';
import { cn } from '../../lib/cn';

const VIEWS = ['table', 'cards', 'gallery', 'kanban'] as const;

type Props = {
  products?: ProductSpec[];
  defaultView?: (typeof VIEWS)[number];
};

function healthColor(score: number) {
  if (score >= 85) return 'text-success-600 bg-success-50 dark:bg-success-950';
  if (score >= 70) return 'text-warning-600 bg-warning-50 dark:bg-warning-950';
  return 'text-error-600 bg-error-50 dark:bg-error-950';
}

export function ProductExplorerPanel({ products = [], defaultView = 'table' }: Props) {
  const [view, setView] = useState<(typeof VIEWS)[number]>(defaultView);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Explorer views">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition',
              view === v
                ? 'bg-brand-600 text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800',
            )}
          >
            {v}
          </button>
        ))}
      </div>

      {view === 'table' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700">
                <th className="pb-2 pr-4">Product</th>
                <th className="pb-2 pr-4">ASIN</th>
                <th className="pb-2 pr-4">Brand</th>
                <th className="pb-2 pr-4">Health</th>
                <th className="pb-2">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-4 font-medium">{p.title}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-slate-500">{p.asin}</td>
                  <td className="py-2 pr-4">{p.brand}</td>
                  <td className="py-2 pr-4">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', healthColor(p.health))}>
                      {p.health}
                    </span>
                  </td>
                  <td className="py-2">₹{(p.revenue / 1000).toFixed(1)}K</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'cards' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <p className="font-medium text-sm">{p.title}</p>
              <p className="mt-1 text-xs text-slate-500">{p.brand} · {p.asin}</p>
              <div className="mt-2 flex items-center justify-between">
                <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', healthColor(p.health))}>
                  Health {p.health}
                </span>
                <span className="text-xs text-slate-500 capitalize">{p.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'gallery' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((p) => (
            <div key={p.id} className="aspect-square rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900">
              <div className="flex h-full flex-col justify-end">
                <p className="text-xs font-medium line-clamp-2">{p.title}</p>
                <span className={cn('mt-1 inline-block w-fit rounded-full px-1.5 py-0.5 text-xs', healthColor(p.health))}>
                  {p.health}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'kanban' && (
        <div className="grid gap-3 sm:grid-cols-3">
          {(['active', 'review', 'draft'] as const).map((col) => (
            <div key={col} className="rounded-lg border border-slate-200 p-2 dark:border-slate-700">
              <p className="mb-2 text-xs font-semibold uppercase capitalize text-slate-500">{col}</p>
              <ul className="space-y-2">
                {products.filter((p) => p.status === col).map((p) => (
                  <li key={p.id} className="rounded border border-slate-100 bg-white p-2 text-xs dark:border-slate-800 dark:bg-slate-900">
                    {p.title}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
